import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BillGrid from './BillGrid';
import BillModal from './BillModal';
import BillsPreview from './BillsPreview';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import SupplierModal from '../suppliers/SupplierModal';
import { useModal } from '../../../hooks/useModal';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import { useDebounce } from '../../../hooks/useDebounce';
import * as billService from '../../../services/billService';
import * as supplierService from '../../../services/supplierService';

/**
 * Self-contained Bills feature view.
 * 
 * Includes:
 *  - The bill grid listing
 *  - The bill modal (create/edit)
 *  - The bill preview sub-view (PDF viewer)
 *  - Draft-restore logic (when returning from preview back to the modal)
 */
export default function BillsView({ importFromPurchase, onClearImport }) {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [setup, setSetup] = useState([]);
  const [setup2, setSetup2] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  // Sub-view state: 'grid' (default) or 'preview'
  const [subView, setSubView] = useState('grid');
  const [draftBill, setDraftBill] = useState(null);
  const [draftToRestore, setDraftToRestore] = useState(null);
  const [createdSupplierId, setCreatedSupplierId] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();
  const createSupplierModal = useModal();
  const periodFilter = usePeriodFilter();

  const filteredBills = useMemo(
    () => periodFilter.filterByPeriod(bills, 'bill_date'),
    [bills, periodFilter],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await billService.fetchBills(debouncedSearchTerm);
      setBills(result.bills);
      setSuppliers(result.suppliers);
      setItems(result.items);
      setTaxes(result.taxes);
      setSetup(result.setup);
      setSetup2(result.setup2);
    } catch (err) {
      console.error('Error fetching bills:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!loading && importFromPurchase) {
      setDraftToRestore(importFromPurchase);
      editModal.open(null);
      onClearImport?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, importFromPurchase]);

  const handleSave = async (formData) => {
    try {
      await billService.saveBill(formData, editModal.record?.bill_id || null);
      editModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await billService.deleteBill(deleteModal.record.bill_id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCreateSupplier = async (formData) => {
    try {
      const created = await supplierService.createSupplier(formData);
      setSuppliers((prev) => {
        const next = [...(prev || []), created];
        next.sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || ''));
        return next;
      });
      setCreatedSupplierId(created?.supplier_id ?? null);
      createSupplierModal.close();
    } catch (err) {
      alert('Failed to save: ' + (err.message || String(err)));
    }
  };

  // Preview flow: modal -> preview
  const handlePreview = (draft) => {
    setDraftBill(draft);
    setSubView('preview');
    editModal.close();
  };

  // Back from preview -> reopen modal with draft data
  const handleBackFromPreview = (draft) => {
    setSubView('grid');
    setDraftToRestore(draft);
    // Reopen the modal with the original record (if editing)
    const record = draft?.formData?.bill_id != null
      ? bills.find((b) => b.bill_id === draft.formData.bill_id) ?? null
      : null;
    editModal.open(record);
  };

  // Preview sub-view
  if (subView === 'preview') {
    return (
      <BillsPreview
        draftBill={draftBill}
        onBack={() => handleBackFromPreview(draftBill)}
      />
    );
  }

  // Grid view (default)
  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search bills..."
        addLabel="New Bill"
        addColor="bg-teal-500 hover:bg-teal-600"
        period={periodFilter.period}
        onPeriodChange={periodFilter.onPeriodChange}
        dateFrom={periodFilter.dateFrom}
        onDateFromChange={periodFilter.onDateFromChange}
        dateTo={periodFilter.dateTo}
        onDateToChange={periodFilter.onDateToChange}
      />

      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <BillGrid
            rowData={filteredBills}
            onEdit={(r) => editModal.open(r)}
            onDelete={(r) => deleteModal.open(r)}
            onGridReady={setGridApi}
          />
        )}
      </div>

      <BillModal
        isOpen={editModal.isOpen}
        onClose={() => { editModal.close(); setDraftToRestore(null); setCreatedSupplierId(null); }}
        onSubmit={handleSave}
        initialData={editModal.record}
        draftToRestore={draftToRestore}
        onClearDraftRestore={() => setDraftToRestore(null)}
        suppliers={suppliers}
        items={items}
        taxes={taxes}
        setup={setup}
        setup2={setup2}
        onPreview={handlePreview}
        bills={bills}
        onCreateSupplier={() => createSupplierModal.open(null)}
        createdSupplierId={createdSupplierId}
        onQuickBooksSynced={(patch) => {
          fetchData();
          if (
            patch?.bill_id != null &&
            editModal.isOpen &&
            editModal.record?.bill_id === patch.bill_id
          ) {
            editModal.open({
              ...editModal.record,
              quickbooks_bill_id: patch.quickbooks_bill_id ?? editModal.record.quickbooks_bill_id,
              quickbooks_synced_at: patch.quickbooks_synced_at ?? editModal.record.quickbooks_synced_at,
            });
          }
        }}
        onXeroSynced={(patch) => {
          fetchData();
          if (
            patch?.bill_id != null &&
            editModal.isOpen &&
            editModal.record?.bill_id === patch.bill_id
          ) {
            editModal.open({
              ...editModal.record,
              xero_bill_id: patch.xero_bill_id ?? editModal.record.xero_bill_id,
              xero_synced_at: patch.xero_synced_at ?? editModal.record.xero_synced_at,
            });
          }
        }}
      />

      <SupplierModal
        isOpen={createSupplierModal.isOpen}
        onClose={createSupplierModal.close}
        onSubmit={handleCreateSupplier}
        initialData={createSupplierModal.record}
        title="Add New Supplier"
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={
          deleteModal.record?.bill_number
            ? `Bill #${deleteModal.record.bill_number}`
            : 'this bill'
        }
      />
    </>
  );
}
